import { type DataObjectsManager, type IDataObject, type OrderBy, type Query } from '@inpart-io/devtools-iap';
import { RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import { type BoardLayout } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { getDateTime } from '@/services/date-format';

import { DEFAULT_PAGINATION, DEFAULT_SEARCH_KEY } from './constants';
import { GET_ORDER_BY, GET_PAGE_COUNT, GET_PAGE_SIZE, GET_ROWS_COUNT, GET_START_INDEX } from './getters';
import {
    RECEIVE_CURRENT_PAGE,
    RECEIVE_LAYOUT,
    RECEIVE_ORDER_BY,
    RECEIVE_PAGE_SIZE,
    RECEIVE_PAGINATION,
    RECEIVE_PAGINATION_TIMESTAMP,
    RECEIVE_ROW_COUNT,
    RECEIVE_SEARCH_RESULTS,
    RECEIVE_SEARCH_TIMESTAMP,
    RECEIVE_VISIBLE_COLUMNS,
    RECENTLY_ADDED_SEARCH_RESULTS,
    REMOVE_PAGINATION,
    REMOVE_SEARCH_RESULT_LIST,
    UPDATE_SEARCH_RESULTS
} from './mutations';
import { type ISearchState } from './types';

export const SAVE_SEARCH_RESULT = 'saveSearchResult';
export const ADD_SEARCH_RESULT = 'addSearchResult';
export const REMOVE_SEARCH_RESULT = 'removeSearchResult';
export const SAVE_PAGINATION = 'savePagination';
export const SETUP_PAGINATION = 'setupPagination';
export const DELETE_PAGINATION = 'deletePagination';
export const DELETE_SEARCH_RESULT = 'deleteSearchResult';
export const PAGINATED_SEARCH = 'paginatedSearch';
export const REFRESH_CURRENT_PAGE = 'refreshCurrentPage';
export const UPDATE_PAGINATION_TIMESTAMP = 'updatePaginationTimestamp';
export const UPDATE_CURRENT_PAGE = 'updateCurrentPage';
export const UPDATE_PAGE_SIZE = 'updatePageSize';
export const UPDATE_VISIBLE_COLUMNS = 'updateVisibleColumns';
export const UPDATE_LAYOUT = 'updateLayout';
export const UPDATE_ORDER_BY = 'updateOrderBy';
export const UPDATE_ROWS_COUNT = 'updateRowsNumber';
export const COUNT_BY_QUERY = 'countByQuery';
export const INCREMENT_COUNT = 'incrementCount';

export function getActions<T extends IDataObject>(manager: DataObjectsManager<T>): ActionTree<ISearchState, unknown> {
    return {
        [DELETE_SEARCH_RESULT](context, { key = DEFAULT_SEARCH_KEY }: { key: string }) {
            context.commit(REMOVE_SEARCH_RESULT_LIST, { key });
        },
        [SAVE_SEARCH_RESULT](context, { key = DEFAULT_SEARCH_KEY, idList }: { key: string; idList: number[] }): void {
            context.commit(RECEIVE_SEARCH_RESULTS, { key, idList });
        },
        [ADD_SEARCH_RESULT](context, { key = DEFAULT_SEARCH_KEY, idList, atEnd }: { key: string; idList: number[]; atEnd?: boolean }): void {
            if (idList?.length > 0) {
                context.commit(RECENTLY_ADDED_SEARCH_RESULTS, { key, idList, atEnd });
            }
        },
        [REMOVE_SEARCH_RESULT](context, { key = DEFAULT_SEARCH_KEY, idList }: { key: string; idList: number[] }): void {
            if (idList?.length > 0) {
                context.commit(UPDATE_SEARCH_RESULTS, { key, listToRemove: idList });
            }
        },
        [UPDATE_CURRENT_PAGE](context, { key = DEFAULT_SEARCH_KEY, currentPage }: { key: string; currentPage?: number }): void {
            const pageCount = context.getters[GET_PAGE_COUNT](key);
            if (currentPage < 1) {
                context.commit(RECEIVE_CURRENT_PAGE, { key, currentPage: 1 });
            }
            if (pageCount >= currentPage) {
                context.commit(RECEIVE_CURRENT_PAGE, { key, currentPage });
            }
        },
        [UPDATE_PAGE_SIZE](context, { key = DEFAULT_SEARCH_KEY, pageSize }: { key: string; pageSize?: number }): void {
            context.commit(RECEIVE_PAGE_SIZE, { key, pageSize });
        },
        [UPDATE_VISIBLE_COLUMNS](context, { key = DEFAULT_SEARCH_KEY, visibleColumns }: { key: string; visibleColumns?: string[] }): void {
            context.commit(RECEIVE_VISIBLE_COLUMNS, { key, visibleColumns });
        },
        [UPDATE_LAYOUT](context, { key = DEFAULT_SEARCH_KEY, layout }: { key: string; layout?: BoardLayout }): void {
            context.commit(RECEIVE_LAYOUT, { key, layout });
        },
        [UPDATE_ROWS_COUNT](context, { key = DEFAULT_SEARCH_KEY, rowsNumber = 0 }: { key: string; rowsNumber: number }): void {
            const total = rowsNumber < 0 ? 0 : rowsNumber;
            context.commit(RECEIVE_ROW_COUNT, { key, rowsNumber: total });
        },
        [REFRESH_CURRENT_PAGE](context, args: { key?: string }): void {
            if (args?.key !== undefined) {
                context.commit(RECEIVE_PAGINATION_TIMESTAMP, { key: args.key });
            } else {
                context.commit(RECEIVE_SEARCH_TIMESTAMP, {});
            }
        },
        [UPDATE_PAGINATION_TIMESTAMP](context, { key = DEFAULT_SEARCH_KEY }: { key: string }): void {
            const timestamp = getDateTime().toISOString();
            context.commit(RECEIVE_PAGINATION_TIMESTAMP, { key, timestamp });
        },
        [UPDATE_ORDER_BY](context, { key = DEFAULT_SEARCH_KEY, orderBy }: { key: string; orderBy?: OrderBy[] }): void {
            context.commit(RECEIVE_ORDER_BY, { key, orderBy });
        },
        [SETUP_PAGINATION](context, { key = DEFAULT_SEARCH_KEY, pageSize, orderBy }: { key: string; pageSize?: number; orderBy?: OrderBy[] }) {
            const pagination = Object.assign({}, DEFAULT_PAGINATION, { orderBy });
            if (pageSize !== undefined) {
                pagination.pageSize = pageSize;
            }
            context.commit(RECEIVE_PAGINATION, { key, pagination });
        },
        [DELETE_PAGINATION](context, { key = DEFAULT_SEARCH_KEY }: { key: string }) {
            context.commit(REMOVE_PAGINATION, { key });
        },
        async [PAGINATED_SEARCH](
            context,
            { key = DEFAULT_SEARCH_KEY, query, properties, incremental = false }: { key: string; query: Query; properties: string; incremental?: boolean }
        ): Promise<Array<number[] | number>> {
            const startIndex = context.getters[GET_START_INDEX](key);
            const maxResults = context.getters[GET_PAGE_SIZE](key);
            const orderBy = context.getters[GET_ORDER_BY](key);
            const pageQuery = Object.assign({}, query, { maxResults, startIndex, orderBy });

            const timestamp = getDateTime().toISOString();

            const promises: Array<Promise<number[] | number>> = [];
            promises.push(
                context.dispatch(RETRIEVE_BY_QUERY, { query: pageQuery, properties }).then((idList) => {
                    context.commit(RECEIVE_PAGINATION_TIMESTAMP, { key, timestamp });
                    context.commit(RECEIVE_SEARCH_TIMESTAMP, { timestamp });
                    if (incremental) {
                        context.commit(UPDATE_SEARCH_RESULTS, { key, idList });
                    } else {
                        context.commit(RECEIVE_SEARCH_RESULTS, { key, idList });
                    }
                    return idList;
                })
            );
            promises.push(context.dispatch(COUNT_BY_QUERY, { key, query, properties }));
            return Promise.all(promises);
        },
        async [COUNT_BY_QUERY](
            context,
            { key = DEFAULT_SEARCH_KEY, query = {}, properties }: { key: string; query?: Query; properties?: string }
        ): Promise<number> {
            if (manager !== undefined) {
                const rowsNumber = await manager.count(query);
                context.commit(RECEIVE_ROW_COUNT, { key, rowsNumber });
                return rowsNumber;
            }
            return 0;
        },
        [INCREMENT_COUNT](context, { key = DEFAULT_SEARCH_KEY, increment = 1, properties }: { key: string; increment: number; properties?: string }): number {
            if (manager !== undefined) {
                const rowsNumber: number = context.getters[GET_ROWS_COUNT](key);
                context.commit(RECEIVE_ROW_COUNT, { key, rowsNumber: rowsNumber + increment });
                return rowsNumber;
            }
            return 0;
        }
    };
}
